import { inject, Injectable, Signal, signal } from '@angular/core';
import { ApiDataAccess, ReadonlyHttpApiRepository } from '@softline/core';
import type {
  ProductDecimalDigits,
  ProductDecimalsResponse,
} from '../types/product-decimals';

export const DEFAULT_PRODUCT_DECIMAL_DIGITS: ProductDecimalDigits = {
  quantity: 3,
  price: 2,
  amount: 2,
  weight: 3,
};

const SOFTLINE_API_SALES_META_CONFIGURATION =
  '/sales-document-service/sales-document-service/meta/configuration';

/** Module-level so the static entity definition's renderers can read it
 *  without injection. */
const _productDecimalDigits = signal<ProductDecimalDigits>(
  DEFAULT_PRODUCT_DECIMAL_DIGITS
);

export const productDecimalDigits: Signal<ProductDecimalDigits> =
  _productDecimalDigits.asReadonly();

export function setProductDecimalDigits(digits: Partial<ProductDecimalDigits>): void {
  _productDecimalDigits.set({ ...DEFAULT_PRODUCT_DECIMAL_DIGITS, ...digits });
}

@Injectable({ providedIn: 'root' })
export class ProductDecimalsRepository extends ReadonlyHttpApiRepository<
  ProductDecimalsResponse,
  void
> {
  constructor() {
    super(inject(ApiDataAccess), SOFTLINE_API_SALES_META_CONFIGURATION);
  }
}

/** Loads the decimal-digit configuration once per session; failures keep the
 *  defaults. */
@Injectable({ providedIn: 'root' })
export class ProductDecimalsService {
  private readonly repository = inject(ProductDecimalsRepository);
  private loadStarted = false;

  load(): void {
    if (this.loadStarted) return;
    this.loadStarted = true;
    void this.repository.load().then(
      (config) => {
        if (config?.decimalDigits) setProductDecimalDigits(config.decimalDigits);
      },
      () => undefined
    );
  }
}
